import { Check, ChevronsUpDown, Volume2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

interface SpeakerSelectorProps {
	value?: string;
	onValueChange?: (deviceId: string) => void;
	disabled?: boolean;
	className?: string;
}

export function SpeakerSelector({
	value,
	onValueChange,
	disabled,
	className,
}: SpeakerSelectorProps) {
	const { devices, loading } = useAudioOutputDevices();
	const [selected, setSelected] = useState(value || "");

	useEffect(() => {
		if (value !== undefined) setSelected(value);
	}, [value]);

	const defaultId = devices[0]?.deviceId || "";
	useEffect(() => {
		if (!selected && defaultId) {
			setSelected(defaultId);
			onValueChange?.(defaultId);
		}
	}, [defaultId, selected, onValueChange]);

	const current = devices.find((d) => d.deviceId === selected) ||
		devices[0] || {
			label: loading ? "Loading..." : "No speaker",
			deviceId: "",
		};

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					variant="ghost"
					size="sm"
					className={cn(
						"hover:bg-accent flex w-48 cursor-pointer items-center gap-1.5",
						className,
					)}
					disabled={loading || disabled}
				>
					<Volume2 className="h-4 w-4 flex-shrink-0" />
					<span className="flex-1 truncate text-left">{current.label}</span>
					<ChevronsUpDown className="h-3 w-3 flex-shrink-0" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="center" side="top" className="w-72">
				{loading ? (
					<DropdownMenuItem disabled>Loading devices...</DropdownMenuItem>
				) : (
					devices.map((device) => (
						<DropdownMenuItem
							key={device.deviceId}
							onClick={() => {
								setSelected(device.deviceId);
								onValueChange?.(device.deviceId);
							}}
							onSelect={(e) => e.preventDefault()}
							className="flex items-center justify-between"
						>
							<span className="truncate">{device.label}</span>
							{selected === device.deviceId && (
								<Check className="h-4 w-4 flex-shrink-0" />
							)}
						</DropdownMenuItem>
					))
				)}
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

function useAudioOutputDevices() {
	const [devices, setDevices] = useState<{ deviceId: string; label: string }[]>(
		[],
	);
	const [loading, setLoading] = useState(true);

	const load = useCallback(async () => {
		try {
			setLoading(true);
			const list = await navigator.mediaDevices.enumerateDevices();
			setDevices(
				list
					.filter((d) => d.kind === "audiooutput")
					.map((d) => ({
						deviceId: d.deviceId,
						label:
							d.label.replace(/\s*\([^)]*\)/g, "").trim() ||
							`Speaker ${d.deviceId.slice(0, 8)}`,
					})),
			);
		} catch {
			setDevices([]);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	useEffect(() => {
		navigator.mediaDevices.addEventListener("devicechange", load);
		return () =>
			navigator.mediaDevices.removeEventListener("devicechange", load);
	}, [load]);

	return { devices, loading };
}
